/**
 * Catalog-wide comparison.
 * 1. This product, then other same-brand same-type SKUs (mg / count / flavor)
 *    ranked by Vyvata Score.
 * 2. Other brands of the same type, ranked by Vyvata Score.
 * 3. Other catalog items that share a subcategory or goal_tag (similar
 *    benefits, different type), ranked by Vyvata Score.
 * Duplicate listings of the same product are collapsed; the source is
 * always first and never repeated.
 */
(function (root) {
  const FAMILIES = [
    { key: 'coq10', label: 'CoQ10 / Ubiquinol', aliases: ['coenzyme q10', 'ubiquinone', 'ubiquinol', 'coq10'] },
    { key: 'omega_3', label: 'Omega-3', aliases: ['omega-3', 'omega 3', 'fish oil', 'krill oil'] },
    { key: 'magnesium', label: 'Magnesium', aliases: ['magnesium glycinate', 'mag glycinate', 'mag threonate', 'mag citrate', 'magnesium'] },
    { key: 'vitamin_d', label: 'Vitamin D', aliases: ['vitamin d3', 'vitamin d', 'cholecalciferol', 'vit d'] },
    { key: 'vitamin_c', label: 'Vitamin C', aliases: ['vitamin c', 'ascorbic acid', 'ascorbate'] },
    { key: 'vitamin_b12', label: 'Vitamin B12', aliases: ['methylcobalamin', 'cyanocobalamin', 'cobalamin', 'b-12', 'b12'] },
    { key: 'b_complex', label: 'B-complex', aliases: ['b-complex', 'b complex', 'b vitamins'] },
    { key: 'vitamin_k2', label: 'Vitamin K2', aliases: ['menaquinone', 'vitamin k2', 'mk-7', 'mk7'] },
    { key: 'multivitamin', label: 'Multivitamin', aliases: ['multivitamin', 'multi-vitamin', 'daily multi'] },
    { key: 'iron', label: 'Iron', aliases: ['iron bisglycinate', 'ferrous', 'ferritin', 'iron'] },
    { key: 'zinc', label: 'Zinc', aliases: ['zinc'] },
    { key: 'calcium', label: 'Calcium', aliases: ['calcium'] },
    { key: 'creatine', label: 'Creatine', aliases: ['creatine'] },
    { key: 'collagen', label: 'Collagen', aliases: ['collagen'] },
    { key: 'probiotic', label: 'Probiotics', aliases: ['probiotic', 'lactobacillus', 'bifidobacterium'] },
    { key: 'turmeric', label: 'Turmeric / curcumin', aliases: ['turmeric', 'curcumin'] },
    { key: 'ashwagandha', label: 'Ashwagandha', aliases: ['ashwagandha', 'ksm-66'] },
    { key: 'melatonin', label: 'Melatonin', aliases: ['melatonin'] },
    { key: 'berberine', label: 'Berberine', aliases: ['berberine'] },
    { key: 'nmn', label: 'NMN', aliases: ['nicotinamide mononucleotide', 'nmn'] },
    { key: 'nr', label: 'Nicotinamide riboside', aliases: ['nicotinamide riboside'] },
    { key: 'nac', label: 'NAC', aliases: ['n-acetyl cysteine', 'n acetyl cysteine', 'acetylcysteine'] },
    { key: 'resveratrol', label: 'Resveratrol', aliases: ['resveratrol'] },
    { key: 'lions_mane', label: "Lion's mane", aliases: ["lion's mane", 'lions mane', 'hericium'] },
    { key: 'rhodiola', label: 'Rhodiola', aliases: ['rhodiola'] },
    { key: 'l_theanine', label: 'L-Theanine', aliases: ['l-theanine', 'theanine'] },
    { key: 'glucosamine', label: 'Glucosamine', aliases: ['glucosamine'] },
    { key: 'fiber', label: 'Fiber', aliases: ['psyllium', 'glucomannan'] },
    { key: 'whey', label: 'Whey protein', aliases: ['whey'] },
  ];

  function asList(value) {
    if (Array.isArray(value)) return value.filter(Boolean).map(String);
    if (value == null || value === '') return [];
    return [String(value)];
  }

  function overlaps(a, b) {
    if (!a.length || !b.length) return false;
    const set = new Set(a);
    return b.some((item) => set.has(item));
  }

  function findByHandle(products, handle) {
    const needle = String(handle || '').toLowerCase();
    return products.find((p) => String(p.handle || '').toLowerCase() === needle) || null;
  }

  function escapeRe(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function productText(p) {
    return [p.title, p.handle, (p.tokens || []).join(' ')]
      .join(' ')
      .toLowerCase()
      .replace(/[_/,]+/g, ' ')
      .replace(/koenzim\s*q[\s-]*10/g, 'coq10')
      .replace(/coenzyme\s*q[\s-]*10/g, 'coq10')
      .replace(/co[\s-]*q[\s-]*10/g, 'coq10');
  }

  function aliasIndex(text, alias) {
    const a = String(alias || '').toLowerCase().trim();
    if (!a) return -1;
    const re = new RegExp('(^|[^a-z0-9])' + escapeRe(a) + '(?=[^a-z0-9]|$)', 'i');
    const m = String(text || '').match(re);
    if (!m) return -1;
    return m.index + m[1].length;
  }

  function hasAlias(text, alias) {
    return aliasIndex(text, alias) >= 0;
  }

  function familiesOf(p) {
    const text = productText(p);
    const hits = [];
    FAMILIES.forEach((fam) => {
      let idx = Infinity;
      fam.aliases.forEach((alias) => {
        const at = aliasIndex(text, alias);
        if (at >= 0 && at < idx) idx = at;
      });
      if (idx !== Infinity) hits.push({ key: fam.key, label: fam.label, idx });
    });
    hits.sort((a, b) => a.idx - b.idx);
    return hits;
  }

  function primaryFamily(p) {
    return familiesOf(p)[0] || null;
  }

  function samePrimaryFamily(sourceFam, peer) {
    if (!sourceFam) return false;
    const peerFam = primaryFamily(peer);
    return !!(peerFam && peerFam.key === sourceFam.key);
  }

  function byScoreDesc(a, b) {
    return (b.score || 0) - (a.score || 0);
  }

  function brandKey(p) {
    const vendor = String(p.vendor || '')
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
    if (vendor) return vendor;
    const title = String(p.title || '');
    const colon = title.indexOf(':');
    if (colon > 1 && colon < 48) {
      return title.slice(0, colon)
        .toLowerCase()
        .replace(/['’]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }
    return '';
  }

  function brandLabel(p) {
    if (p && p.vendor) return String(p.vendor).trim();
    const title = String((p && p.title) || '');
    const colon = title.indexOf(':');
    if (colon > 1 && colon < 48) return title.slice(0, colon).trim();
    return '';
  }

  function sameBrand(a, b) {
    const ka = brandKey(a);
    const kb = brandKey(b);
    return !!(ka && kb && ka === kb);
  }

  function titleKey(p) {
    return String(p.title || '')
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function imageKey(p) {
    const url = String(p.image_url || '').split('?')[0];
    const file = decodeURIComponent(url.split('/').pop() || '').toLowerCase();
    if (!file || file.length < 8) return '';
    return file.replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g, '');
  }

  function packSignature(p) {
    const brand = brandKey(p);
    const fam = primaryFamily(p);
    if (!brand || !fam) return '';
    const text = productText(p);
    const doseM = text.match(/(\d+)\s*(mg|mcg|iu|g)\b/i);
    const countM = text.match(/(\d+)\s*(?:softgels?|capsules?|caps\b|count|\bct\b|tablets?|gummies|vc)\b/i);
    if (!doseM || !countM) return '';
    const form = fam.key === 'coq10' && /\bubiquinol\b/.test(text) ? 'ubiquinol' : fam.key;
    return [brand, fam.key, doseM[1] + doseM[2].toLowerCase(), countM[1], form].join('|');
  }

  function identityKeys(p) {
    const keys = ['h:' + String(p.handle || '').toLowerCase()];
    const title = titleKey(p);
    const brand = brandKey(p);
    if (title) keys.push('t:' + (brand || '') + '|' + title);
    if (title.length >= 24) keys.push('t*:' + title);
    const img = imageKey(p);
    if (img) keys.push('i:' + img);
    const pack = packSignature(p);
    if (pack) keys.push('p:' + pack);
    return keys;
  }

  function takeUnique(seen, list) {
    const out = [];
    list.forEach((p) => {
      const keys = identityKeys(p).filter(Boolean);
      if (keys.some((k) => seen.has(k))) return;
      keys.forEach((k) => seen.add(k));
      out.push(p);
    });
    return out;
  }

  /**
   * @returns {{
   *   source: object,
   *   family: { key: string, label: string } | null,
   *   brand: string,
   *   sameBrand: object[],
   *   otherSameType: object[],
   *   sameType: object[],
   *   similarBenefits: object[],
   *   peers: object[]
   * } | null}
   */
  function similarBenefits(products, handle) {
    const list = Array.isArray(products) ? products : [];
    const source = findByHandle(list, handle);
    if (!source) return null;

    const sourceSubs = asList(source.vsf_subcategory);
    const sourceGoals = asList(source.goal_tags);
    const sourceCat = source.vsf_category || '';
    const family = primaryFamily(source);
    const brand = brandLabel(source);

    const sameBrandList = [];
    const otherSame = [];
    const similar = [];

    list.forEach((p) => {
      if (String(p.handle || '').toLowerCase() === String(source.handle || '').toLowerCase()) {
        return;
      }
      const subMatch = overlaps(sourceSubs, asList(p.vsf_subcategory));
      const goalMatch = overlaps(sourceGoals, asList(p.goal_tags));
      const catMatch = !sourceSubs.length && !sourceGoals.length && sourceCat
        ? (p.vsf_category || '') === sourceCat
        : false;

      if (family) {
        if (samePrimaryFamily(family, p)) {
          if (sameBrand(source, p)) sameBrandList.push(p);
          else otherSame.push(p);
          return;
        }
        if (subMatch || goalMatch || catMatch) similar.push(p);
        return;
      }

      if (subMatch) {
        if (sameBrand(source, p)) sameBrandList.push(p);
        else otherSame.push(p);
        return;
      }
      if (goalMatch || catMatch) similar.push(p);
    });

    sameBrandList.sort(byScoreDesc);
    otherSame.sort(byScoreDesc);
    similar.sort(byScoreDesc);

    const seen = new Set();
    takeUnique(seen, [source]);
    const sameBrandKept = takeUnique(seen, sameBrandList);
    const otherSameKept = takeUnique(seen, otherSame);
    const similarKept = takeUnique(seen, similar);
    const sameType = sameBrandKept.concat(otherSameKept);

    return {
      source,
      family,
      brand,
      sameBrand: sameBrandKept,
      otherSameType: otherSameKept,
      sameType,
      similarBenefits: similarKept,
      peers: sameType.concat(similarKept),
    };
  }

  function rankedList(products, handle) {
    const result = similarBenefits(products, handle);
    if (!result) return [];
    return [result.source].concat(result.peers);
  }

  /**
   * Related-product row: peers only (never the current product). Same brand
   * first, then other same-type brands, then similar benefits, capped at `limit`.
   */
  function relatedList(products, handle, limit) {
    const result = similarBenefits(products, handle);
    if (!result) return [];
    const cap = Math.max(0, Number(limit) || 4);
    return result.peers.slice(0, cap);
  }

  const api = {
    FAMILIES,
    asList,
    overlaps,
    findByHandle,
    productText,
    familiesOf,
    primaryFamily,
    brandKey,
    brandLabel,
    sameBrand,
    identityKeys,
    similarBenefits,
    rankedList,
    relatedList,
  };

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  root.VSFCompare = api;
})(typeof window !== 'undefined' ? window : globalThis);
